import ClickhouseDateTimeGranularity = App.Enums.ClickhouseDateTimeGranularity;
import { OpUnitType } from "dayjs";
import { granularityToDateParser } from "./scaleWeekTicks.js";

const timeGranularities = ["1min", "5min", "10min", "15min", "30min", "60min"];

export function getEffectiveSize(
	width: number,
	height: number,
	rotationDegrees: number
): { effectiveWidth: number; effectiveHeight: number } {
	// Convert to radians
	const theta = (rotationDegrees * Math.PI) / 180;

	const cos = Math.cos(theta);
	const sin = Math.sin(theta);

	// Compute axis-aligned bounding box dimensions
	const effectiveWidth = Math.abs(width * cos) + Math.abs(height * sin);
	const effectiveHeight = Math.abs(width * sin) + Math.abs(height * cos);

	return { effectiveWidth, effectiveHeight };
}

export default function <T>(
	ticks: T[],
	getTickValue: (tick: T) => string,
	modifyTick: (tick: T, keep: boolean, isTime: boolean) => void,
	maxTicks: number,
	granularity: ClickhouseDateTimeGranularity,
	supportsPeriodTicks: boolean
): boolean {
	if (timeGranularities.includes(granularity)) {
		// How many minutes are in the granularity
		const multiplier = parseInt(granularity);
		ticks.forEach((tick, idx) => {
			const label = getTickValue(tick);
			const parts = label.split(":");

			// Multiply the amount of ticks by the amount of minutes in the granularity to scale everything to 1 min resolution.
			// This allows us to re-use the same logic for all time granularities.
			let keep = maxTicks > 0 && idx === ticks.length / 2;
			if ((ticks.length * multiplier) / 5 <= maxTicks) {
				keep = [
					"00",
					"05",
					"10",
					"15",
					"20",
					"25",
					"30",
					"35",
					"40",
					"45",
					"50",
					"55",
				].includes(parts[1]);
			} else if ((ticks.length * multiplier) / 15 <= maxTicks) {
				keep = ["00", "15", "30", "45"].includes(parts[1]);
			} else if ((ticks.length * multiplier) / 30 <= maxTicks) {
				keep = ["00", "30"].includes(parts[1]);
			} else if ((ticks.length * multiplier) / 60 <= maxTicks) {
				keep = parts[1] === "00";
			} else if ((ticks.length * multiplier) / 120 <= maxTicks) {
				keep = parseInt(parts[0]) % 2 === 0 && parts[1] === "00";
			} else if ((ticks.length * multiplier) / 240 <= maxTicks) {
				keep = parseInt(parts[0]) % 4 === 0 && parts[1] === "00";
			} else if ((ticks.length * multiplier) / 360 <= maxTicks) {
				keep = parseInt(parts[0]) % 6 === 0 && parts[1] === "00";
			} else if ((ticks.length * multiplier) / 720 <= maxTicks) {
				keep = parseInt(parts[0]) % 12 === 0 && parts[1] === "00";
			}

			modifyTick(tick, keep, true);
		});

		return true;
	}

	if (!supportsPeriodTicks) {
		return false;
	}

	let approxTicks = ticks.length;
	// When the granularity is month already, we want to align them to quarter next.
	if (granularity === "month") {
		approxTicks *= 30;
	}

	if (approxTicks < maxTicks) {
		ticks.forEach((tick) => {
			modifyTick(tick, true, false);
		});

		return true;
	}

	let startOfPeriod: OpUnitType | "isoWeek";
	if (approxTicks / 7 <= maxTicks && granularity === "day") {
		startOfPeriod = "isoWeek";
	} else if (approxTicks / 30 <= maxTicks) {
		startOfPeriod = "month";
	} else if (approxTicks / (30 * 4) <= maxTicks) {
		startOfPeriod = "quarter";
	} else {
		startOfPeriod = "year";
	}

	const sameUnit: OpUnitType | "isoWeek" = "day";
	// if (granularity === "week") {
	// 	return;
	// } else if (granularity === "month") {
	// 	sameUnit = "month";
	// }

	const dateParser = granularityToDateParser(granularity);
	if (!dateParser) {
		return false;
	}
	const ticksToKeep = ticks.filter((tick) => {
		const label = getTickValue(tick);
		const date = dateParser(label);

		const keep = date.isSame(date.clone().startOf(startOfPeriod), sameUnit);
		if (!keep) {
			// If we don't want to keep it, we can already pass that along to the horizontal/offset.
			modifyTick(tick, false, false);

			return false;
		}

		return true;
	});

	const amountOfTicksToCull = Math.ceil(ticksToKeep.length / maxTicks);

	// Of the ticks to keep, we apply culling logic if maxTicks is less than ticksToKeep.
	// If maxTicks is 0, we don't show any ticks. (amountOfTicksToCull is Infinity).
	ticksToKeep.forEach((tick, idx) => {
		modifyTick(
			tick,
			idx % amountOfTicksToCull === 0 && amountOfTicksToCull !== Infinity,
			false
		);
	});

	return true;
}
